import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, User, CalendarDays, Clock, MapPin, ShieldAlert } from "lucide-react";
import BrandHeader from "@/components/BrandHeader";
import { useI18n } from "@/i18n";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { generateBlueprint } from "@/lib/blueprintEngine";
import { M_INDICES_GOAL_ENUM } from "@/engine/v299/m_indices_sheet11";
import {
  routeInfoFormSubmit,
  readEngineMode,
  createBrowserStorage,
  buildProductionRawInputV299,
  validateProductionFormV299,
  ENGINE_MODE,
} from "@/lib/productionResultStorage";
import {
  claimCommercialReport,
  generateCommercialReport,
  readCommercialAccessToken,
  readCommercialOrderId,
} from "@/lib/commercialAccess";

const BIRTH_TIMES = Object.freeze([
  Object.freeze({ value: "子时 (23:00-01:00)", labelKey: "form.birthZi" }),
  Object.freeze({ value: "丑时 (01:00-03:00)", labelKey: "form.birthChou" }),
  Object.freeze({ value: "寅时 (03:00-05:00)", labelKey: "form.birthYin" }),
  Object.freeze({ value: "卯时 (05:00-07:00)", labelKey: "form.birthMao" }),
  Object.freeze({ value: "辰时 (07:00-09:00)", labelKey: "form.birthChen" }),
  Object.freeze({ value: "巳时 (09:00-11:00)", labelKey: "form.birthSi" }),
  Object.freeze({ value: "午时 (11:00-13:00)", labelKey: "form.birthWu" }),
  Object.freeze({ value: "未时 (13:00-15:00)", labelKey: "form.birthWei" }),
  Object.freeze({ value: "申时 (15:00-17:00)", labelKey: "form.birthShen" }),
  Object.freeze({ value: "酉时 (17:00-19:00)", labelKey: "form.birthYou" }),
  Object.freeze({ value: "戌时 (19:00-21:00)", labelKey: "form.birthXu" }),
  Object.freeze({ value: "亥时 (21:00-23:00)", labelKey: "form.birthHai" }),
  Object.freeze({ value: "不确定 / 未知", labelKey: "form.birthUnknown" }),
]);

const REGIONS = Object.freeze([
  Object.freeze({ value: "MY", labelKey: "form.regionMalaysia" }),
  Object.freeze({ value: "SG", labelKey: "form.regionSingapore" }),
  Object.freeze({ value: "CN", labelKey: "form.regionMainlandChina" }),
  Object.freeze({ value: "HK", labelKey: "form.regionHongKong" }),
  Object.freeze({ value: "MO", labelKey: "form.regionMacao" }),
  Object.freeze({ value: "TW", labelKey: "form.regionTaiwan" }),
  Object.freeze({ value: "OTHER_ASIA", labelKey: "form.regionOtherAsia" }),
  Object.freeze({ value: "EUROPE", labelKey: "form.regionEurope" }),
  Object.freeze({ value: "NORTH_AMERICA", labelKey: "form.regionNorthAmerica" }),
  Object.freeze({ value: "OCEANIA", labelKey: "form.regionOceania" }),
  Object.freeze({ value: "MIDDLE_EAST", labelKey: "form.regionMiddleEast" }),
  Object.freeze({ value: "OTHER", labelKey: "form.regionOther" }),
]);

const GENDER_OPTIONS = Object.freeze([
  Object.freeze({ value: "男", labelKey: "form.genderMale" }),
  Object.freeze({ value: "女", labelKey: "form.genderFemale" }),
]);

const GOAL_LABEL_KEYS = Object.freeze({
  "综合分析": "form.goalComprehensive",
  "事业发展": "form.goalCareer",
  "财富增长": "form.goalWealth",
  "创业发展": "form.goalEntrepreneurship",
  "学习提升": "form.goalLearning",
  "贵人合作": "form.goalCollaboration",
  "稳定生活": "form.goalStability",
  "个人品牌": "form.goalBrand",
  "销售沟通": "form.goalSales",
  "内容创作": "form.goalContent",
  "管理领导": "form.goalLeadership",
  "客户关系": "form.goalClientRelations",
});

const SAMPLES = [
  {
    labelKey: "form.sampleA",
    data: { name: "张明", birthDate: "1990-05-12", birthTime: "辰时 (07:00-09:00)", gender: "男", region: "CN" },
  },
  {
    labelKey: "form.sampleB",
    data: { name: "李雨欣", birthDate: "1995-11-03", birthTime: "亥时 (21:00-23:00)", gender: "女", region: "MY" },
  },
  {
    labelKey: "form.sampleC",
    data: { name: "陈家豪", birthDate: "1985-02-27", birthTime: "午时 (11:00-13:00)", gender: "男", region: "HK" },
  },
];
const EMPTY_FORM = Object.freeze({
  name: "",
  birthDate: "",
  birthTime: "",
  birthTimeExact: "",
  gender: "",
  region: "",
  currentGoal: "",
  referenceYear: "",
});

function readSavedFormInput() {
  try {
    if (typeof localStorage === "undefined") {
      return { ...EMPTY_FORM };
    }

    const cached = localStorage.getItem("lb.input");
    if (!cached) {
      return { ...EMPTY_FORM };
    }

    const parsed = JSON.parse(cached);

    if (
      parsed === null ||
      typeof parsed !== "object" ||
      Array.isArray(parsed)
    ) {
      return { ...EMPTY_FORM };
    }

    const readString = (key) =>
      typeof parsed[key] === "string" ? parsed[key] : "";

    return {
      name: readString("name"),
      birthDate: readString("birthDate"),
      birthTime: readString("birthTime"),
      birthTimeExact: readString("birthTimeExact"),
      gender: readString("gender"),
      region: readString("region"),
      currentGoal: readString("currentGoal"),
      referenceYear: readString("referenceYear"),
    };
  } catch (_) {
    return { ...EMPTY_FORM };
  }
}
export default function InfoForm() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [form, setForm] = useState(() => readSavedFormInput());
  const [submitting, setSubmitting] = useState(false);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    const storage = createBrowserStorage();
    const mode = readEngineMode(storage);

    if (mode === ENGINE_MODE.MOCK) {
      const outcome = routeInfoFormSubmit({
        mode,
        form,
        runApplication: null,
        runMock: generateBlueprint,
        storage,
      });

      if (!outcome.ok) {
        toast.error(t("form.missingTitle"), {
          description: t("form.missingMock"),
        });
        return;
      }

      toast.success(t("form.successTitle"), { description: t("form.successDescription") });
      setTimeout(() => navigate("/dashboard"), 400);
      return;
    }

    const validation = validateProductionFormV299(form);
    if (!validation.ok) {
      toast.error(t("form.missingTitle"), {
        description: t("form.missingProduction"),
      });
      return;
    }

    const orderId = readCommercialOrderId();
    const accessToken = readCommercialAccessToken();
    if (!orderId || !accessToken) {
      navigate("/checkout", { replace: true });
      return;
    }

    setSubmitting(true);
    try {
      const rawInput = buildProductionRawInputV299(form);
      await claimCommercialReport(orderId, accessToken, rawInput);
      await generateCommercialReport(orderId, accessToken);

      // Browser storage is convenience only. Mongo report.input and the READY
      // snapshot are the authority from R2-B2 onward.
      try {
        storage.setItem("lb.input", JSON.stringify(form));
        storage.setItem("lb.engineMode", ENGINE_MODE.V299);
        storage.removeItem("lb.result.v299");
      } catch (_) {
        // Do not invalidate a successful server-side immutable report.
      }

      toast.success(t("form.successTitle"), { description: t("form.successDescription") });
      setTimeout(() => navigate("/dashboard"), 400);
    } catch (error) {
      if (error && error.status === 404) {
        navigate("/checkout", { replace: true });
        return;
      }
      toast.error(t("form.engineErrorTitle"), {
        description: `${t("form.engineErrorPrefix")}${(error && error.message) || t("form.unknownError")}`,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const applySample = (data) => {
    setForm({
      ...data,
      birthTimeExact: data.birthTimeExact ?? "",
      currentGoal: "",
      referenceYear: "",
    });

    toast(t("form.sampleAppliedTitle"), {
      description: t("form.sampleAppliedDescription"),
    });
  };

  return (
    <div className="min-h-screen bg-navy-900 relative" data-testid="info-form-page">
      <div className="pointer-events-none absolute inset-0 opacity-50 bg-watermark-compass" />
      <div className="pointer-events-none absolute inset-0 opacity-[0.18] bg-grid" />

      <div className="relative z-10">
        <BrandHeader />

        <main className="max-w-3xl mx-auto px-6 lg:px-10 py-14">
          <div className="fade-up">
            <div className="text-[10px] tracking-[0.35em] uppercase font-mono text-gold-400/80">
              {t("form.eyebrow")}
            </div>
            <h1 className="mt-3 font-serif-cn text-3xl sm:text-4xl text-gold-100">
              {t("form.title")}
            </h1>
            <p className="mt-3 text-sm sm:text-base text-slate-300 leading-relaxed">
              {t("form.introBeforeBrand")}<span className="text-gold-200">Life Blueprint</span>{t("form.introAfterBrand")}
            </p>
          </div>

          <div className="hairline-gold my-8" />

          {/* Sample profiles */}
          <div className="mb-8" data-testid="sample-picker">
            <div className="text-xs tracking-widest uppercase font-mono text-gold-400/80 mb-3">
              {t("form.samplePicker")}
            </div>
            <div className="flex flex-wrap gap-2">
              {SAMPLES.map((s, idx) => (
                <button
                  key={s.labelKey}
                  type="button"
                  data-testid={`sample-profile-${idx}`}
                  onClick={() => applySample(s.data)}
                  className="btn-ghost-gold rounded-full px-4 py-2 text-xs"
                >
                  {t(s.labelKey)}
                </button>
              ))}
            </div>
          </div>

          <form
            onSubmit={handleSubmit}
            className="card-premium rounded-2xl p-6 sm:p-8 space-y-6"
            data-testid="info-form"
          >
            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="name" className="text-gold-200 flex items-center gap-2">
                <User className="w-3.5 h-3.5" /> {t("form.name")}
              </Label>
              <Input
                id="name"
                data-testid="input-name"
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
                placeholder={t("form.namePlaceholder")}
                className="bg-navy-800 border-gold-500/25 text-gold-100 placeholder:text-slate-500 focus-visible:ring-gold-500/40"
              />
            </div>

            {/* Date + Time */}
            <div className="grid sm:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="birthDate" className="text-gold-200 flex items-center gap-2">
                  <CalendarDays className="w-3.5 h-3.5" /> {t("form.birthDate")}
                </Label>
                <Input
                  id="birthDate"
                  data-testid="input-birth-date"
                  type="date"
                  value={form.birthDate}
                  onChange={(e) => update("birthDate", e.target.value)}
                  className="bg-navy-800 border-gold-500/25 text-gold-100 focus-visible:ring-gold-500/40"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-gold-200 flex items-center gap-2">
                  <Clock className="w-3.5 h-3.5" /> {t("form.birthTime")}
                </Label>
                <Select value={form.birthTime} onValueChange={(v) => update("birthTime", v)}>
                  <SelectTrigger
                    data-testid="select-birth-time"
                    className="bg-navy-800 border-gold-500/25 text-gold-100"
                  >
                    <SelectValue placeholder={t("form.birthTimePlaceholder")} />
                  </SelectTrigger>
                  <SelectContent className="bg-navy-800 border-gold-500/30 text-gold-100">
                    {BIRTH_TIMES.map((option) => (
                      <SelectItem
                        key={option.value}
                        value={option.value}
                        className="focus:bg-gold-500/10 focus:text-gold-100"
                      >
                        {t(option.labelKey)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="pt-2">
                  <Label
                    htmlFor="birthTimeExact"
                    className="text-gold-200/90 text-[11px] tracking-wide flex items-center gap-2"
                  >
                    {t("form.birthTimeExact")}
                  </Label>
                  <Input
                    id="birthTimeExact"
                    data-testid="input-birth-time-exact"
                    type="time"
                    step="60"
                    value={form.birthTimeExact}
                    onChange={(e) => update("birthTimeExact", e.target.value)}
                    className="mt-1.5 bg-navy-800 border-gold-500/25 text-gold-100 focus-visible:ring-gold-500/40"
                  />
                  <p
                    data-testid="birth-time-exact-hint"
                    className="mt-1.5 text-[10px] text-slate-400 leading-relaxed"
                  >
                    {t("form.birthTimeExactHint")}
                  </p>
                </div>
              </div>
            </div>
{/* Task 3C-6 · Production Input Contract preparation */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-gold-200">
                  {t("form.currentGoal")}
                </Label>

                <Select
                  value={form.currentGoal}
                  onValueChange={(value) => update("currentGoal", value)}
                >
                  <SelectTrigger
                    data-testid="select-current-goal"
                    className="bg-navy-800 border-gold-500/25 text-gold-100"
                  >
                    <SelectValue placeholder={t("form.currentGoalPlaceholder")} />
                  </SelectTrigger>

                  <SelectContent className="bg-navy-800 border-gold-500/30 text-gold-100">
                    {M_INDICES_GOAL_ENUM.map((goal) => (
                      <SelectItem key={goal} value={goal}>
                        {t(GOAL_LABEL_KEYS[goal])}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="referenceYear" className="text-gold-200">
                  {t("form.referenceYear")}
                </Label>

                <Input
                  id="referenceYear"
                  data-testid="input-reference-year"
                  type="number"
                  min="1"
                  step="1"
                  value={form.referenceYear}
                  onChange={(e) => update("referenceYear", e.target.value)}
                  placeholder={t("form.referenceYearPlaceholder")}
                  className="bg-navy-800 border-gold-500/25 text-gold-100"
                />
              </div>

              <p className="text-xs text-slate-400">
                {t("form.productionFieldsHint")}
              </p>
            </div>

            {/* Gender */}
            <div className="space-y-3">
              <Label className="text-gold-200">{t("form.gender")}</Label>
              <RadioGroup
                value={form.gender}
                onValueChange={(v) => update("gender", v)}
                className="flex gap-4"
                data-testid="radio-gender"
              >
                {GENDER_OPTIONS.map((option) => (
                  <label
                    key={option.value}
                    htmlFor={`g-${option.value}`}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full border cursor-pointer transition ${
                      form.gender === option.value
                        ? "border-gold-500/70 bg-gold-500/10 text-gold-100"
                        : "border-gold-500/25 text-slate-300 hover:border-gold-500/45"
                    }`}
                  >
                    <RadioGroupItem
                      id={`g-${option.value}`}
                      value={option.value}
                      data-testid={`radio-gender-${option.value}`}
                      className="border-gold-500/60 text-gold-300"
                    />
                    <span className="text-sm">{t(option.labelKey)}</span>
                  </label>
                ))}
              </RadioGroup>
            </div>

            {/* Region */}
            <div className="space-y-2">
              <Label className="text-gold-200 flex items-center gap-2">
                <MapPin className="w-3.5 h-3.5" /> {t("form.region")}
              </Label>
              <Select value={form.region} onValueChange={(v) => update("region", v)}>
                <SelectTrigger
                  data-testid="select-region"
                  className="bg-navy-800 border-gold-500/25 text-gold-100"
                >
                  <SelectValue placeholder={t("form.regionPlaceholder")} />
                </SelectTrigger>
                <SelectContent className="bg-navy-800 border-gold-500/30 text-gold-100">
                  {REGIONS.map((option) => (
                    <SelectItem
                      key={option.value}
                      value={option.value}
                      className="focus:bg-gold-500/10 focus:text-gold-100"
                    >
                      {t(option.labelKey)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="hairline-gold" />

            <div className="flex items-start gap-3">
              <ShieldAlert className="w-4 h-4 text-gold-400/90 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-slate-400 leading-relaxed">
                {t("form.disclaimer")}
              </p>
            </div>

            <div className="flex flex-wrap gap-3 pt-2">
              <button
                type="submit"
                disabled={submitting}
                data-testid="form-submit-button"
                className="btn-gold disabled:opacity-50 rounded-full px-8 py-3.5 inline-flex items-center gap-2 text-sm"
              >
                {t("form.submit")}
                <ArrowRight className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => setForm({ ...EMPTY_FORM })}
                data-testid="form-reset-button"
                className="btn-ghost-gold rounded-full px-6 py-3 text-sm"
              >
                {t("form.reset")}
              </button>
            </div>
          </form>
        </main>
      </div>
    </div>
  );
}
